/**
 * Oynatıcı ekranı.
 *
 * Akış:
 *   1. `usePlaybackPlan` → karar (doğrudan mı, transcode mu) ve iş durumu
 *   2. kaynak hazır → `attachSource` (doğrudan: Range, HLS: hls.js)
 *   3. izleme oturumu → **sunucunun bildirdiği aralıkta** ilerleme raporu
 *
 * Rapor aralığı istemcide sabit yazılı değildir: `capabilities.progress.reportIntervalMs`
 * (spec §izleme raporu — gözlenen değer 10 sn).
 */

import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ApiError,
  type AudioTrack,
  api,
  type Capabilities,
  type Item,
  type PlaybackDecision,
  type Subtitle,
  type TranscodeJob,
} from '../api.ts';
import { audioTrackLabel, pickDefaultAudioTrack } from '../audioTracks.ts';
import { PlayerBar } from '../components/PlayerBar.tsx';
import { ErrorNotice } from '../components/States.tsx';
import { clientProfileId } from '../deviceProfile.ts';
import { displayTitle, formatDuration } from '../format.ts';
import {
  AlertIcon,
  ArrowLeftIcon,
  BoltIcon,
  ClockIcon,
  CloseIcon,
  Forward10Icon,
  FullscreenExitIcon,
  HourglassIcon,
  InfoIcon,
  LockIcon,
  MusicIcon,
  RefreshIcon,
  Replay10Icon,
  SearchIcon,
  SettingsIcon,
} from '../Icon.tsx';
import { directSource, hlsSource, type PlaybackSource } from '../playback.ts';
import {
  effectivePlaybackPrefs,
  MODE_LABELS,
  type PlaybackMode,
  type PlaybackQuality,
  QUALITY_LABELS,
  usePlaybackPrefs,
} from '../playbackPrefs.ts';
import { clearProgressDraft } from '../progressDraft.ts';
import {
  SUBTITLE_MIN_BOTTOM_PX,
  useControlsSafePx,
  usePictureBounds,
  useStageMetrics,
} from '../subtitleStyle.ts';
import { useAttach } from '../useAttachSource.ts';
import { useAutoAdvance } from '../useAutoAdvance.ts';
import { useBufferingState } from '../useBufferingState.ts';
import { type ElementRef, useElementRef } from '../useElementRef.ts';
import { useFullscreen } from '../useFullscreen.ts';
import { useLandscapeLock } from '../useLandscapeLock.ts';
import { useMediaSession } from '../useMediaSession.ts';
import { useNextItem } from '../useNextItem.ts';
import { usePlaybackPlan } from '../usePlaybackPlan.ts';
import { usePlayerControls } from '../usePlayerControls.ts';
import { useProgressDraft } from '../useProgressDraft.ts';
import { useSubtitleOverlay } from '../useSubtitleOverlay.ts';
import { useSubtitlePrefs } from '../useSubtitlePrefs.ts';
import { useSubtitleStatus } from '../useSubtitleStatus.ts';
import { useTouchSeek } from '../useTouchSeek.ts';
import { useVideoAspect } from '../useVideoAspect.ts';
import { useWatchResume } from '../useWatchResume.ts';
import { useVideoFit } from '../videoFit.ts';

type ReportState = 'playing' | 'buffering' | 'paused' | 'stopped';

/** Görünmez kontrol katmanlarını odak/erişilebilirlik ağacından çıkarır. */
function inertWhen(hidden: boolean): true | undefined {
  return hidden ? true : undefined;
}

/** Yavaş bağlantıda bir kademe düşük kalite; 480p'de öneri yok. */
function lowerQualityFor(quality: PlaybackQuality): '720p' | '480p' | null {
  if (quality === '480p') return null;
  if (quality === '720p') return '480p';
  return '720p';
}

/**
 * "Kaliteyi düşür" önerisi **işe yarar mı?**
 *
 * ## Neden koşula bağlı?
 *
 * Öneri yalnız bağlantının dar olduğu durumda anlamlıdır: akışı küçültmek
 * bant genişliğini rahatlatır. Ama **kopya (remux)** akışta küçültme yoktur:
 * video olduğu gibi kopyalanır (`-c:v copy`), yani 720p'ye düşmek akışı
 * küçültmez — yalnız **CPU harcar** ve görüntüyü bozar.
 *
 * Aynı şey **soğuk katman** (ev tüneli / rclone) için de geçerli: darboğaz
 * kaynağın okunmasıdır, kodlama değil. 720p'ye düşen bir kopyalama işi,
 * 1080p kopyalama işinden **daha yavaş** olur (aynı baytlar okunur, üstüne
 * kodlama eklenir).
 *
 * ## Kullanıcının bildirdiği hata
 *
 * "Yarım kalan film tekrar orijinal çözünürlükten başlamıyor, 720p'ye
 * düşüyor." Sebep: devam etme akışı soğuk katmanda **yeni bir iş** açmak
 * zorunda (konumdan kopyalanacak) → kaynak uzak diskten yavaş okunuyor →
 * oynatıcı "Bağlantı yavaş" bandını gösteriyor → kullanıcı (haklı olarak
 * "yavaş" görüp) kaliteyi düşürüyor → film 720p'de açılıyor.
 *
 * Doğrusu: bu durumda kaliteyi düşürmek **hızlandırmaz**. Bu yüzden öneri
 * gizlenir, yerine ne olduğu açıklanır.
 */
function canLowerQuality(decision: PlaybackDecision | null): boolean {
  if (decision === null) return true;
  // Kopya akışta küçültme yok → düşürmek işe yaramaz.
  if (decision.targetProfile === 'remux') return false;
  // Soğuk katman: darboğan kaynağın okunması, kodlama değil.
  if (decision.layer === 'cold') return false;
  return true;
}

function BufferingOverlay({
  state,
  lowerQuality,
  isCopyOrCold,
  onLowerQuality,
}: {
  state: { visible: boolean; slow: boolean };
  lowerQuality: '720p' | '480p' | null;
  /** Kopya ya da soğuk katman: kaliteyi düşürmek hızlandırmaz. */
  isCopyOrCold: boolean;
  onLowerQuality: (quality: '720p' | '480p') => void;
}): ReactNode {
  if (!state.visible) return null;
  return (
    <div
      className={`watch__buffering ${state.slow ? 'watch__buffering--slow' : ''}`}
      role="status"
      aria-live="polite"
    >
      <span className="watch__buffering-spinner" aria-hidden="true" />
      <div className="watch__buffering-copy">
        <strong>
          {state.slow
            ? isCopyOrCold
              ? 'Sunucu akışı hazırlıyor'
              : 'Bağlantı yavaş'
            : 'Akış yükleniyor'}
        </strong>
        {state.slow && (
          <span>
            {isCopyOrCold
              ? 'Kaynak uzak diskten okunuyor; kaliteyi düşürmek hızlandırmaz.'
              : 'Kaliteyi düşürerek devam edebilirsiniz.'}
          </span>
        )}
      </div>
      {state.slow && lowerQuality !== null && !isCopyOrCold && (
        <button
          type="button"
          className="button button--small watch__buffering-action"
          onClick={() => onLowerQuality(lowerQuality)}
        >
          {lowerQuality}&apos;ye düşür
        </button>
      )}
    </div>
  );
}

function SeekFeedbackOverlay({
  feedback,
}: {
  feedback: { deltaMs: number; token: number } | null;
}): ReactNode {
  if (feedback === null) return null;
  return (
    <div
      key={feedback.token}
      className={`watch__seek-feedback watch__seek-feedback--${
        feedback.deltaMs > 0 ? 'forward' : 'back'
      }`}
      role="status"
      aria-live="polite"
    >
      {feedback.deltaMs > 0 ? <Forward10Icon size={30} /> : <Replay10Icon size={30} />}
      <span>{feedback.deltaMs > 0 ? '+10 sn' : '-10 sn'}</span>
    </div>
  );
}

/**
 * Video sınıfı ve gerçek oranı tek yerde.
 *
 * `aspect-ratio` kaynağın `videoWidth/videoHeight` değerinden gelir; sabit
 * 16:9 kutu, 4:3/anamorfik kaynaklarda yanlarda gereksiz siyah bant
 * bırakıyordu.
 */
function videoPresentation(
  fit: 'contain' | 'cover',
  aspect: { css: string } | null,
): { className: string; style: { aspectRatio: string } | undefined } {
  return {
    className: fit === 'cover' ? 'watch__video watch__video--cover' : 'watch__video',
    style: aspect === null ? undefined : { aspectRatio: aspect.css },
  };
}

/**
 * Oturum açarken sunucuya bildirilen profil.
 *
 * **Tek kaynak:** `usePlaybackPlan` ile aynı olmalı. Bu dosyada ikinci bir
 * sabit tanımlıydı; mobil profil seçimi yapıldığında ikisi ayrışabiliyordu
 * (karar mobil profille, oturum masaüstü profille açılıyordu).
 */
const PROFILE_ID = clientProfileId();

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: oynatıcı; plan, kaynak, tam ekran ve raporlama durumlarını tek yerde yönetir
export function WatchScreen({
  item,
  capabilities,
  startOver = false,
  onClose,
  onPlayNext,
}: {
  item: Item;
  capabilities: Capabilities;
  /** "Baştan başlat": kayıtlı ilerlemeyi yok sayar. */
  startOver?: boolean;
  onClose: () => void;
  /**
   * Sıradaki içeriğe geç (müzik parçası / bölüm).
   *
   * Boş bırakılırsa "sıradaki" düğmesi **pasif** kalır: yönlendirme bu
   * ekranın işi değil, kabuğun (`App`) işidir.
   */
  onPlayNext?: (itemId: string) => void;
}): ReactNode {
  /**
   * **Geç bağlanan** öğe: `<video>` ancak oynatma hazır olduğunda DOM'a girer.
   * `useEffect([videoRef])` deseni bu yüzden sessizce hiç çalışmaz — bu hata
   * sınıfı üç kez gerçek sunucuda oynatmayı bozdu. Bkz. `useElementRef`.
   */
  const videoRef = useElementRef<HTMLVideoElement>();
  const { fit: videoFit, toggle: toggleVideoFit } = useVideoFit();
  const videoAspect = useVideoAspect(videoRef.element);
  const { userId, resumeMs } = useWatchResume(item, startOver);
  const sessionRef = useRef<string | null>(null);
  const startedAtRef = useRef<number | null>(null);
  const positionRef = useRef(0);

  /**
   * Canlı oynatma konumu — **öğe kimliğiyle birlikte**.
   *
   * Kalite düşürmesinde plan yeniden çalışır ve film baştan kodlanmamalıdır.
   * Öğe kimliği tutulur çünkü içerik değişirken eski `<video>` bir an daha
   * DOM'da kalabilir; eski öğenin saniyesi yeni filme taşınmamalı.
   */
  const livePositionRef = useRef<{ itemId: string; ms: number }>({ itemId: item.id, ms: 0 });
  /** Kullanıcı akışın başlangıcından öncesine sardı: sıradaki plan buradan başlar. */
  const forcedStartRef = useRef<{ itemId: string; ms: number } | null>(null);
  const getStartPositionMs = useCallback((itemId: string): number => {
    const forced = forcedStartRef.current;
    if (forced !== null && forced.itemId === itemId) {
      forcedStartRef.current = null;
      return forced.ms;
    }
    return livePositionRef.current.itemId === itemId ? livePositionRef.current.ms : 0;
  }, []);

  const audioTracks = item.technical?.audioTracks ?? [];
  const defaultAudioIndex = pickDefaultAudioTrack(audioTracks);
  const [selectedAudioIndex, setSelectedAudioIndex] = useState(defaultAudioIndex);
  const [forceCompatibility, setForceCompatibility] = useState(false);
  /**
   * Kullanıcı oynatma tercihleri (ADR-011): kalite ve yöntem.
   *
   * Tercih değişince karar **yeniden** verilir (`usePlaybackPlan` tercihe
   * bağlıdır) — yani "orijinal" seçimi anında etkili olur.
   */
  const playbackPrefs = usePlaybackPrefs();
  /**
   * Oturum kapsamlı kalite geçersiz kılması.
   *
   * ## Neden ayrı?
   *
   * "Bağlantı yavaş → 720p'ye düşür" eylemi **kalıcı** tercihi değiştiriyordu.
   * Kullanıcı bir kez tıkladığında cihaz 720p'e kilitleniyor ve **bundan
   * sonraki tüm filmler** 720p hedefiyle açılıyordu — kullanıcı bunu
   * istememişti, o an sadece o filmin yavaş açılmasını istememişti.
   *
   * Kalıcı tercih yalnız **tercih panelinden** değişir; buradaki eylem
   * yalnız bu oynatma oturumunu etkiler (ekran kapandığında biter).
   */
  const [sessionQuality, setSessionQuality] = useState<PlaybackQuality | null>(null);
  const effectivePrefs = useMemo(
    () => effectivePlaybackPrefs(playbackPrefs.prefs, sessionQuality),
    [playbackPrefs.prefs, sessionQuality],
  );
  const [retryKey, setRetryKey] = useState(0);
  const {
    decision,
    job,
    status,
    failure,
    startPositionMs: planStartMs,
  } = usePlaybackPlan(
    item,
    forceCompatibility,
    effectivePrefs,
    selectedAudioIndex,
    retryKey,
    getStartPositionMs,
    // "Baştan başlat" seçildiyse `useWatchResume` 0 döner; plan da bunu
    // kullanmalı, yoksa kayıtlı ilerleme yine başlangıç olur.
    resumeMs,
  );
  /**
   * Kaynağın başlayacağı konum.
   *
   * Plan **canlı konumu** taşır (kalite değişimi / yeniden deneme); plan henüz
   * yoksa kayıtlı ilerlemeye düşülür. Oynatıcı ve izleme oturumu aynı değeri
   * kullanır ki film başa sarmasın.
   */
  const startMs = planStartMs ?? resumeMs;
  const [notice, setNotice] = useState<string | null>(null);
  const [playerError, setPlayerError] = useState<string | null>(null);
  const [position, setPosition] = useState(0);

  /**
   * Sıradaki içerik.
   *
   * Kural sunucuda çözülür (`/api/items/:id/next`): müzik kütüphanesinde
   * sıradaki **parça**, dizide sıradaki **bölüm**, filmde sıradaki **yok**.
   */
  const nextState = useNextItem(item.id, onPlayNext);

  /*
   * Kilit ekranı / medya bildirimi (Öneri 7'nin küçük parçası).
   * Metadata ve play/pause/next eylemleri bağlanır; desteklemeyen
   * tarayıcıda sessizce devre dışı kalır.
   */
  useMediaSession({ item, video: videoRef.element, onNext: nextState.playNext });

  const reportIntervalMs = capabilities.progress.reportIntervalMs;
  const source = useMemo(
    () =>
      resolveSource(
        decision,
        job,
        status?.state ?? null,
        status?.playable === true,
        item.id,
        status?.actualStartMs ?? null,
      ),
    /**
     * `status` bağımlılığı **nesne** değil, oynatmayı etkileyen **ilkel
     * değerler** olmalı.
     *
     * Regresyon (gerçek sunucuda bulundu): `status`, 1 saniyelik yoklamayla
     * her turda **yeni bir nesne** olarak geliyor. Onu bağımlılık yapmak
     * `useMemo`'yu her saniye çalıştırıyor, `source` her saniye yeni nesne
     * oluyor, `useAttach` effect'i yeniden kuruluyor ve oynatma **her saniye
     * baştan başlıyordu**.
     *
     * `resolveSource` yalnız `state`, `playable` ve **ölçülen başlangıç**
     * alanlarına bakar; `progressRatio` gibi diğerleri oynatılabilir kaynağı
     * etkilemez.
     */
    [decision, job, status?.state, status?.playable, status?.actualStartMs, item.id],
  );

  /**
   * Altyazı izleri.
   *
   * Yalnız `softPlayable` olanlar `<track>` olarak eklenir; bitmap biçimler
   * (pgs, vobsub) tarayıcıda oynatılamaz — sunucu yakma gerektiğini bildirir
   * (not 03). Kullanıcının seçimi korunur (otomatik seçim yalnız ilk açılışta).
   *
   * Seçim **altyazı anahtarıyla** tutulur (`e3` gömülü, `s0` yan dosya) —
   * iki kaynak tek sayı alanında karışmasın (ADR-016).
   */
  const [activeTrack, setActiveTrack] = useState<string | null>(() =>
    pickDefaultTrack(item.subtitles ?? []),
  );
  const subtitleStates = useSubtitleStatus(item.id);
  const tracks = useMemo(
    () =>
      (item.subtitles ?? []).map((track: Subtitle) => ({
        ...track,
        selected: track.key === activeTrack,
        state: subtitleStates[track.key] ?? (track.softPlayable ? 'idle' : 'burn_required'),
      })),
    [item.subtitles, activeTrack, subtitleStates],
  );

  /**
   * Altyazı bindirmesi.
   *
   * `<track>` etiketleri **yalnız doğrudan oynatmada** çalışır; hls.js'in
   * kullandığı MSE yolunda sessizce yoksayılırlar. Gerçek sunucuda 40+ izli
   * bir film transcode edildi, kullanıcı iz seçti, listede de göründü —
   * ekranda hiçbir şey çıkmadı. Sunucu tarafı sorunsuzdu.
   *
   * Bu yüzden altyazı sunucunun WebVTT çıktısı çekilip oynatıcının üstüne
   * yazılıyor; iki yolda da aynı davranış.
   */
  const overlay = useSubtitleOverlay(videoRef.element, item.id, activeTrack);

  /**
   * Tam ekran hedefi `<video>` değil, `.watch__stage` kutusudur.
   *
   * Tarayıcı tam ekranı **en üst katmana** taşır ve `::backdrop` ile ekranı
   * kaplar; katmana çıkmayan kardeşler boyanmaz. Altyazı bindirmesi
   * `<video>`'nun kardeşi olduğu için yerel tam ekranda kayboluyordu.
   *
   * Yerel düğme `controlsList` ile gizlenir (destekleniyorsa) ve yerine
   * aşağıdaki düğme konur. Bkz. `useFullscreen`.
   */
  /**
   * Altyazı ölçeği **video yüksekliğine** bağlıdır.
   *
   * Ölçüm JS tarafında yapılır: CSS'te doğru birim (`cqh`) `container-type:
   * size` ister, o da tam ekran hedefi olan kutuya `contain: size` uygulayıp
   * genişliğini 0 yapardı. Bkz. `subtitleStyle.ts`.
   *
   * Kutuya **geri çağırma referansıyla** bağlanır (`attach`), nesne ref
   * değil: oynatma hazır değilken bu div henüz DOM'da yok, nesne ref kullanan
   * bir `useEffect` bir daha tetiklenmez ve ölçüm hep `null` kalır.
   */
  const stage = useStageMetrics();

  /**
   * Kontrollerin görünürlüğü **konumdan önce** bilinmeli: altyazının alt
   * sınırı buna göre değişiyor (aşağıda). Sıralama bu yüzden bilinçli.
   */
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [isControlsVisible, setIsControlsVisible] = useState(true);
  const idleTimerRef = useRef<number | null>(null);

  /*
   * Bekleme göstergesi. `waiting` video olaylarından gelir; kısa takılmalar
   * spinner göstermez, uzun beklemede kalite düşürme önerisi çıkar.
   */
  const [isWaiting, setIsWaiting] = useState(false);
  const buffering = useBufferingState(isWaiting);

  /*
   * Mobil çift dokunma: sol yarı -10 sn, sağ yarı +10 sn.
   *
   * Tek dokunma kontrolleri açıp kapatır; çift dokunmayı ondan ayırmak için
   * tek dokunma `SINGLE_TAP_DELAY_MS` kadar bekletilir. Aksi hâlde ilk dokunma
   * kontrolleri hemen kapatır, ikinci dokunma videoyu sarar ve ekranda
   * kontrolsüz bir görünürlük değişimi olur.
   */
  const [seekFeedback, setSeekFeedback] = useState<{ deltaMs: number; token: number } | null>(null);
  const seekFeedbackTimerRef = useRef<number | null>(null);

  const showSeekFeedback = useCallback((deltaMs: number): void => {
    setSeekFeedback({ deltaMs, token: Date.now() });
    if (seekFeedbackTimerRef.current !== null) {
      window.clearTimeout(seekFeedbackTimerRef.current);
    }
    seekFeedbackTimerRef.current = window.setTimeout(() => {
      setSeekFeedback(null);
      seekFeedbackTimerRef.current = null;
    }, 700);
  }, []);

  useEffect(() => {
    return () => {
      if (seekFeedbackTimerRef.current !== null) {
        window.clearTimeout(seekFeedbackTimerRef.current);
      }
    };
  }, []);

  // Öneri, **etkin** kaliteye göre hesaplanır (oturum geçersiz kılması varsa o).
  const lowerQuality = lowerQualityFor(effectivePrefs.quality);
  // Kopya ya da soğuk katmanda kaliteyi düşürmek hızlandırmaz → öneri gizlenir.
  const isCopyOrCold = !canLowerQuality(decision);

  /**
   * Altyazı **konumu** resmin kendisine göre hesaplanır.
   *
   * Sinema oranlı (2.35:1) bir film 16:9 kutuda oynatıldığında altta siyah
   * bant oluşur; eski sabit yüzde (`bottom: max(12%, 92px)`) o bandı
   * hesaba katmadığı için altyazı görüntünün ortasında kalıyor ve kullanıcı
   * aşağı çekemiyordu. Bkz. `usePictureBounds`.
   */
  const pictureBounds = usePictureBounds(stage.element, videoRef.element, videoFit);
  /** Kontrol çubuğunun kapladığı yükseklik: altyazının alt sınırı. */
  const measuredControlsSafePx = useControlsSafePx(stage.element);
  /*
   * Kontroller görünürken zemindir, gizlenince serbest bırakılır.
   *
   * Kontrol çubuğu (~120 px) alt siyah banttan (~100 px) **daha yüksek**;
   * zemini hep kontrol çubuğu tutarsak "siyah banta indir" ayarı hiçbir işe
   * yaramaz. Oynatma sırasında kontrol çubuğu zaten kendiliğinden gizlenir,
   * altyazı da o sırada bantın içine yerleşir.
   */
  const controlsSafePx = isControlsVisible ? measuredControlsSafePx : SUBTITLE_MIN_BOTTOM_PX;

  /*
   * Kutu ve video **geç bağlanır** (oynatma hazır değilken DOM'da yoktur).
   * Bu yüzden kanca `element` durumuyla birlikte verilir: `useFullscreen`
   * etiketi `[stage.element, video.element]` bağımlılıklarıyla yeniden
   * kurar ve `fullscreenchange` dinleyicisi gerçekten bağlanır.
   *
   * Eskiden `stage.ref`/`videoRef.ref` (nesne referansı) veriliyordu; etiket
   * mount anında `null` görüp hiç çalışmıyor, "tam ekrandan dön" yolu
   * kullanılmaz hale geliyordu. Bkz. `useFullscreen`.
   */
  const fullscreen = useFullscreen(stage, videoRef);
  useLandscapeLock(fullscreen.isFullscreen, videoAspect);
  // Yerel düğmeyi gizleyebildiğimiz tarayıcılarda `nofullscreen` uygulanır;
  // desteklenmeyende düğme görünür kalır ve `fullscreenchange` hedefi
  // düzeltir — iki durumda da altyazı kaybolmaz.
  const subtitlePrefs = useSubtitlePrefs(stage.heightPx, {
    stageHeightPx: stage.heightPx,
    bounds: pictureBounds,
    controlsSafePx,
  });

  /**
   * Oynatma denetimleri.
   *
   * `onPosition` izleme raporunu besler: konum saniyede bir güncellenir
   * (`timeupdate` ~4 Hz gelir, rapor için yeterli; her karede göndermek
   * gereksiz istek demek).
   */
  const onPosition = useCallback(
    (ms: number) => {
      livePositionRef.current = { itemId: item.id, ms };
      positionRef.current = ms;
      setPosition((previous) =>
        Math.floor(previous / 1000) === Math.floor(ms / 1000) ? previous : ms,
      );
    },
    [item.id],
  );

  const toggleSubtitles = useCallback(() => {
    setActiveTrack((current) => {
      if (current !== null) return null;
      const first =
        tracks.find((t) => t.state === 'ready') ?? tracks.find((t) => t.softPlayable) ?? tracks[0];
      return first ? first.key : null;
    });
  }, [tracks]);

  /**
   * Akışın başlangıcından öncesine sarma: oynatıcıyı zorlamak yerine yeni bir
   * plan başlat. `getStartPositionMs` bu değeri bir kez tüketir; sonraki
   * kalite değişimleri yine canlı konumdan devam eder.
   */
  const restartAt = useCallback(
    (ms: number): void => {
      forcedStartRef.current = { itemId: item.id, ms };
      setRetryKey((value) => value + 1);
    },
    [item.id],
  );

  const controls = usePlayerControls(videoRef, {
    // `durationMs` null olabilir (probe başarısız); kanca oynatıcının bildirdiği
    // süreye düşer.
    totalMs: item.durationMs ?? 0,
    onPosition,
    /*
     * Konumdan başlayan transcode'da segmentler ör. 600. saniyeden başlar.
     * 600'ün altına atlamak mevcut akışla mümkün değildir; istek yeni bir iş
     * açar (film yine baştan kodlanmaz, istenen saniyeden kodlanır).
     */
    seekFloorMs: job?.startPositionMs ?? 0,
    onSeekBeforeStart: restartAt,
    onToggleFullscreen: fullscreen.toggle,
    onToggleHelp: () => setShowShortcuts((s) => !s),
    onToggleSubtitles: toggleSubtitles,
    onNext: nextState.playNext,
  });

  /*
   * Parça bitince sıradakine otomatik geçiş (yalnız müzik parçaları).
   *
   * Kural `useAutoAdvance` içinde; burada yalnız durum bağlanır.
   */
  useAutoAdvance({
    isEnded: controls.isEnded,
    positionMs: controls.positionMs,
    relation: nextState.relation,
    hasNext: nextState.hasNext,
    onNext: nextState.playNext,
  });

  const resetIdleTimer = useCallback(() => {
    setIsControlsVisible(true);
    if (idleTimerRef.current !== null) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (controls.isPlaying) {
      idleTimerRef.current = window.setTimeout(() => {
        setIsControlsVisible(false);
      }, 3500);
    }
  }, [controls.isPlaying]);

  useEffect(() => {
    if (!controls.isPlaying || showShortcuts) {
      setIsControlsVisible(true);
      if (idleTimerRef.current !== null) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    } else {
      resetIdleTimer();
    }
    return () => {
      if (idleTimerRef.current !== null) {
        window.clearTimeout(idleTimerRef.current);
      }
    };
  }, [controls.isPlaying, showShortcuts, resetIdleTimer]);

  /**
   * Kontrolleri aç/kapat.
   *
   * Masaüstünde fare hareketi zaten kontrolleri gösterir; **dokunmatik**
   * cihazda hareket yoktur. Sadece "dokununca göster" kuralı uygulansaydı
   * kullanıcı kontrolleri bir daha gizleyemiyordu. Dokunma bu yüzden
   * **aç/kapat** yapar.
   */
  const toggleControls = useCallback(() => {
    if (!controls.isPlaying) {
      resetIdleTimer();
      return;
    }
    if (isControlsVisible) {
      setIsControlsVisible(false);
      if (idleTimerRef.current !== null) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      return;
    }
    resetIdleTimer();
  }, [controls.isPlaying, isControlsVisible, resetIdleTimer]);

  useTouchSeek(stage.element, {
    onSeek: controls.seekRelative,
    onToggleControls: toggleControls,
    onActivity: resetIdleTimer,
    onFeedback: showSeekFeedback,
  });

  useAttach(videoRef, source, startMs, { setNotice, setError: setPlayerError, retryKey });
  useReporting(
    videoRef,
    source,
    item.id,
    startMs,
    reportIntervalMs,
    sessionRef,
    startedAtRef,
    positionRef,
    userId,
  );

  useProgressDraft(videoRef, userId, item.id, item.durationMs);

  const report = useCallback(
    (state: ReportState) => {
      const sessionId = sessionRef.current;
      // Olay geri çağrısı **render'dan sonra** çalışır, bu yüzden nesne ref'ini
      // tembel okumak doğru ve `useCallback` kimliğini sabit tutar. `element`
      // bağımlılığı yalnız **etiketlerde** gerekir (aşağıya bakın).
      const video = videoRef.ref.current;
      if (sessionId === null || video === null) return;

      const sourceMs = Math.round(video.currentTime * 1000);
      positionRef.current = sourceMs;
      const startedAt = startedAtRef.current ?? Date.now();

      void api
        .reportProgress(sessionId, {
          sessionPositionMs: Date.now() - startedAt, // oturum içi geçen süre
          sourcePositionMs: sourceMs, // kaynak içindeki gerçek konum
          state,
        })
        .then(() => {
          if (state === 'stopped' && userId !== null) clearProgressDraft(userId, item.id);
        })
        .catch(() => undefined);
    },
    [videoRef.ref, userId, item.id],
  );

  /**
   * Aktif transcode işinin **son** durumu — temizlik sırasında güncel değeri
   * okumak için ref'te tutulur.
   *
   * Efekt yalnızca **iş kimliğine** bağlanmalı. `status.state` bağımlılığa
   * girerse `queued → running` geçişinde React önceki efektin temizliğini
   * çalıştırır ve sunucudaki işi iptal eder (gerçek regresyon: kuyruktaki iş
   * başlar başlamaz ölüyordu).
   */
  const activeJobId = job?.jobId ?? null;
  const activeJobStateRef = useRef<{ jobId: string | null; state: string | null }>({
    jobId: activeJobId,
    state: status?.state ?? job?.state ?? null,
  });
  activeJobStateRef.current = {
    jobId: activeJobId,
    state: status?.state ?? job?.state ?? null,
  };

  // Oynatıcı kapandığında veya plan değiştiğinde sunucudaki kodlamayı sonlandır
  useEffect(() => {
    if (activeJobId === null) return;

    const handlePageHide = (): void => {
      const current = activeJobStateRef.current;
      if (current.jobId !== activeJobId) return;
      if (current.state === 'queued' || current.state === 'running') {
        void api.cancelJobKeepalive(activeJobId).catch(() => undefined);
      }
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      // Plan değiştiyse ya da ekran kapandıysa eski işi bırakma.
      void api.cancelJob(activeJobId).catch(() => undefined);
    };
  }, [activeJobId]);

  const close = (): void => {
    // Kullanıcı ekranı kapattığında sunucuda devam eden FFmpeg işini derhal iptal et
    if (job !== null && (job.state === 'queued' || job.state === 'running')) {
      void api.cancelJob(job.jobId).catch(() => undefined);
    }
    onClose();
  };

  const retryPlayback = (): void => {
    setPlayerError(null);
    setRetryKey((value) => value + 1);
  };

  const selectAudio = (index: number): void => {
    setSelectedAudioIndex(index);
    // Doğrudan oynatmada ses izi seçimi tarayıcılar arasında güvenilir değil;
    // varsayılan dışındaki parçalar sunucuda seçilerek dönüştürülür.
    setForceCompatibility(index !== defaultAudioIndex);
  };

  const blockingError = failure ?? playerError;
  if (blockingError !== null) {
    return (
      <WatchErrorScreen
        message={blockingError}
        decision={decision}
        prefs={effectivePrefs}
        onQualityChange={(quality) => {
          // Panelden yapılan değişiklik **kalıcıdır**; oturum geçersiz
          // kılması temizlenir (artık kalıcı tercih belirleyicidir).
          setSessionQuality(null);
          playbackPrefs.setQuality(quality);
        }}
        onModeChange={playbackPrefs.setMode}
        onRetry={retryPlayback}
        onBack={close}
      />
    );
  }

  if (source === null) {
    return (
      <Preparing
        job={job}
        progressRatio={status?.progressRatio ?? null}
        startPositionMs={startMs}
        isRemoteSource={decision?.layer === 'cold'}
        onCancel={close}
      />
    );
  }

  return (
    <div className="watch">
      <span className="visually-hidden" role="status" aria-live="polite">
        Şimdi oynatılıyor: {displayTitle(item)}
      </span>
      <div className="watch__ambient-glow" aria-hidden="true" />
      <div className="watch__theater">
        {/*
          Altyazı artık `<track>` ile değil, **bindirmeyle** gösteriliyor:
          `<track>` yalnız doğrudan oynatmada uygulanır, MSE/HLS yolunda
          sessizce yok sayılır. Bu yüzden `useSubtitleOverlay` WebVTT'yi
          çekip oynatıcının üstüne yazar. Bkz. o dosyanın başlığı.
        */}
        {/*
          Video + altyazı bindirmesi aynı konumlandırma kutusunda
          (`.watch__stage`, `position: relative`). Bindirme `absolute` olduğu
          için video'nun yüksekliğini (`aspect-ratio`) bilmesi gerekir.
          Aynı kutu **tam ekran hedefidir**: `<video>` tam ekrana girerse
          bindirme kardeş olarak katmana çıkmaz ve ekranda kaybolur.
        */}
        <div
          className={`watch__stage ${!isControlsVisible ? 'watch__stage--idle' : ''} ${fullscreen.isFullscreen ? 'is-fullscreen' : ''}`}
          ref={stage.attach}
          style={stage.style}
        >
          {fullscreen.isFullscreen && isControlsVisible && (
            <button
              type="button"
              className="watch__fullscreen-back"
              onClick={fullscreen.toggle}
              aria-label="Tam ekrandan çık"
              title="Tam ekrandan çık"
            >
              <ArrowLeftIcon size={18} />
              <span>Geri</span>
            </button>
          )}

          {/* Üst Karartma ve Başlık Katmanı */}
          <div
            className="watch__header-overlay"
            aria-hidden={!isControlsVisible}
            inert={inertWhen(!isControlsVisible)}
          >
            <div className="watch__header-info">
              <h2 className="watch__header-title">{displayTitle(item)}</h2>
              <span className="watch__header-mode">{describeMode(decision)}</span>
            </div>
            <button
              type="button"
              className="button button--ghost watch__header-close"
              onClick={close}
              aria-label="Oynatmayı Kapat"
              title="Kapat (Esc)"
            >
              <CloseIcon size={16} />
              <span className="watch__header-close-text">Kapat</span>
            </button>
          </div>

          {/* Alt Gradyan Karartması */}
          <div className="watch__scrim-bottom" aria-hidden="true" />

          {isAudioMedia(item, decision) && <AudioVisualizer item={item} />}

          <video
            ref={videoRef.attach}
            {...videoPresentation(videoFit, videoAspect)}
            tabIndex={0}
            playsInline
            onPause={() => {
              setIsWaiting(false);
              report('paused');
            }}
            onPlay={() => {
              setIsWaiting(false);
              report('playing');
            }}
            onCanPlay={() => setIsWaiting(false)}
            onSeeked={() => setIsWaiting(false)}
            onWaiting={() => {
              setIsWaiting(true);
              report('buffering');
            }}
            onStalled={() => setIsWaiting(true)}
            onEnded={() => {
              setIsWaiting(false);
              report('stopped');
            }}
          />

          <BufferingOverlay
            state={buffering}
            lowerQuality={lowerQuality}
            isCopyOrCold={isCopyOrCold}
            /*
             * Kalıcı tercih **değişmez**: bu eylem yalnız bu oynatmayı etkiler.
             * Kullanıcı bir kez tıkladığında cihazın tüm filmleri 720p'e
             * kilitlenmesi bugün yaşanan ve istenmeyen bir durumdu.
             */
            onLowerQuality={setSessionQuality}
          />
          <SeekFeedbackOverlay feedback={seekFeedback} />
          {overlay.loading && (
            <div className="watch__subtitles-loading" role="status">
              <span className="watch__spinner" aria-hidden="true" />
              Altyazı hazırlanıyor…
            </div>
          )}

          {overlay.text !== null && (
            <div className="watch__subtitles" style={subtitlePrefs.style} aria-live="polite">
              {overlay.text}
            </div>
          )}

          {fullscreen.isFullscreen && (
            <button
              type="button"
              className="watch__fullscreen"
              onClick={fullscreen.toggle}
              aria-label="Tam ekrandan çık"
              title="Tam ekrandan çık (Esc)"
              inert={inertWhen(!isControlsVisible)}
            >
              <FullscreenExitIcon size={18} />
            </button>
          )}

          <PlayerBar
            controls={controls}
            totalMs={controls.totalMs}
            isFullscreen={fullscreen.isFullscreen}
            onToggleFullscreen={fullscreen.toggle}
            videoFit={videoFit}
            onToggleVideoFit={toggleVideoFit}
            controlsInert={!isControlsVisible}
            tracks={tracks}
            onSelectTrack={(key) => setActiveTrack(key)}
            subtitlePrefs={subtitlePrefs}
            showShortcuts={showShortcuts}
            onToggleShortcuts={() => setShowShortcuts((s) => !s)}
            /*
              "Sıradaki" düğmesi: kural sunucuda çözülür, burada yalnız
              sonuç (var/yok + başlık) taşınır. Kabuk yönlendirme sağlamazsa
              düğme pasif kalır.
            */
            hasNext={nextState.hasNext}
            nextTitle={nextState.next?.title ?? null}
            onNext={nextState.playNext}
            /*
              Menü portalının hedefi oynatıcı kutusu: mobilde alt sayfa
              menüsü kutunun `overflow: hidden` sınırından taşmalı, tam ekranda
              da en üst katmanda kalmalı. Bkz. `PlayerBar`.
            */
            overlayContainer={stage.element}
          />
        </div>

        {/* Oynatıcı Altı: Modern Bilgi ve Akış Kartı */}
        <div className="watch__info-card">
          <div className="watch__info-main">
            <div className="watch__info-header">
              <h1 className="watch__title">{displayTitle(item)}</h1>
              <WatchBadges
                item={item}
                decision={decision}
                position={position}
                forceCompatibility={forceCompatibility}
                onToggleCompatibility={() => setForceCompatibility((prev) => !prev)}
              />
            </div>

            <button type="button" className="button button--ghost watch__exit-btn" onClick={close}>
              <CloseIcon size={16} />
              Kapat
            </button>
          </div>

          {notice !== null && (
            <div className="watch__alert">
              <InfoIcon size={16} />
              {notice}
            </div>
          )}

          {overlay.error !== null && (
            <div className="watch__alert watch__alert--warn" role="status">
              <AlertIcon size={16} />
              <div style={{ flex: 1 }}>{overlay.error}</div>
              {/*
                Eylem **hata türüne göre** değişir.

                415 (bitmap): dönüştürmekten başka yol yok → uyumluk modu.
                504 (zaman aşımı): dosya **okunamadı**, dönüştürülmedi → aynı
                isteği tekrarlamak yeterli; "Uyumluluk Modunu Aç" burada
                yanlış öneri olurdu (kullanıcı gereksiz encode başlatırdı).
              */}
              {overlay.errorKind === 'burn_required' && (
                <button
                  type="button"
                  className="button button--small"
                  style={{ marginLeft: '12px', whiteSpace: 'nowrap' }}
                  onClick={() => setActiveTrack(null)}
                  title="Bu altyazıyı kapat ve videoyu altyazısız oynat"
                >
                  <CloseIcon size={14} />
                  Altyazısız devam et
                </button>
              )}
              {overlay.retryable && (
                <button
                  type="button"
                  className="button button--small"
                  style={{ marginLeft: '12px', whiteSpace: 'nowrap' }}
                  onClick={overlay.retry}
                  disabled={overlay.loading}
                  title="Altyazı isteğini yeniden gönder"
                >
                  {overlay.loading ? (
                    <>
                      <HourglassIcon size={14} />
                      Bekleniyor…
                    </>
                  ) : (
                    <>
                      <RefreshIcon size={14} />
                      Tekrar Dene
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          {decision !== null && decision.reasons.length > 0 && <ReasonList decision={decision} />}

          {/*
            Oynatma tercihleri (ADR-011).

            Kullanıcı kaliteyi ve yöntemi kendisi seçer; sunucu "mümkünse
            kopyala" ilkesiyle CPU'yu olabildiğince az kullanır. Seçim
            değişince karar yeniden verilir ve oynatma kaynağı yeniden
            çözülür.
          */}
          <PlaybackPrefsPanel
            prefs={effectivePrefs}
            decision={decision}
            audioTracks={audioTracks}
            selectedAudioIndex={selectedAudioIndex}
            onAudioChange={selectAudio}
            onQualityChange={(quality) => {
              // Panelden yapılan değişiklik **kalıcıdır**; oturum geçersiz
              // kılması temizlenir.
              setSessionQuality(null);
              playbackPrefs.setQuality(quality);
            }}
            onModeChange={playbackPrefs.setMode}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Karar ve iş durumundan oynatılabilir kaynağı türetir (`null` = henüz hazır değil).
 *
 * `statusState` bilerek **durum nesnesi değil, `state` string'idir**: durum
 * her 1 saniyede yeni bir nesne olarak geliyor ve `useMemo` bütün nesneyi
 * bağımlılık olarak alırsa `source` her saniy yeniden üretilir.
 */
function resolveSource(
  decision: PlaybackDecision | null,
  job: TranscodeJob | null,
  statusState: string | null,
  playable: boolean,
  itemId: string,
  actualStartMs: number | null,
): PlaybackSource | null {
  if (decision === null) return null;
  if (decision.mode !== 'transcode') {
    return directSource(decision.streamUrl ?? api.streamUrl(itemId));
  }
  // Sunucu ilk segmentleri hazır olduğunda `playable` yükselir; `state`
  // ancak FFmpeg bitince `ready` olur. İkisi de oynatmayı başlatır —
  // böylece 2 saatlik bir film için transcode süresince beklemek gerekmez.
  if ((statusState === 'ready' || playable) && job !== null) {
    /*
     * Zaman çizelgesi **ölçülen** başlangıca göre kurulur.
     *
     * FFmpeg giriş araması kaynağın en yakın IDR'sine atlar; HEVC'de bu
     * istenenden saniyeler önce olabilir (ölçüm: istenen 600.0, gerçek
     * 591.4). Sunucunun bildirdiği istenen konum kullanılırsa zaman çizelgesi
     * içerikle uyuşmaz ve oynatma takılır.
     */
    const merged = actualStartMs === null ? job : { ...job, actualStartMs };
    return hlsSource(merged);
  }
  return null;
}

/**
 * İzleme oturumu ve 10 saniyelik rapor.
 *
 * Kapanışta son konum bir kez daha yazılır; "izlendi" işareti yalnız
 * `stopped` durumunda ve eşik aşılınca oluşur (spec §izleme raporu).
 */
function useReporting(
  videoRef: ElementRef<HTMLVideoElement>,
  source: PlaybackSource | null,
  itemId: string,
  startPositionMs: number,
  reportIntervalMs: number,
  sessionRef: React.RefObject<string | null>,
  startedAtRef: React.RefObject<number | null>,
  positionRef: React.RefObject<number>,
  userId: string | null,
): void {
  const currentSourceUrlRef = useRef<string | null>(null);

  /**
   * `source` artık referans olarak kararlı (`useMemo` + `status?.state`).
   *
   * Regresyon (gerçek sunucuda bulundu): önceden `source` her render'da yeni
   * nesne olduğu için effect her render'da yeniden kuruluyor, cleanup
   * `state:'stopped'` raporu gönderip oturumu sıfırlıyor ve effect
   * `startSession()` **tekrar** çağırıyordu — yani her render yeni bir izleme
   * oturumu açılıyordu. Konsolda saniyede iki ayrı oturum görülmüştü.
   */
  useEffect(() => {
    if (source === null) return;
    if (videoRef.element === null) return;
    if (currentSourceUrlRef.current === source.url) return;
    currentSourceUrlRef.current = source.url;

    let cancelled = false;

    void api
      .startSession(itemId, PROFILE_ID, startPositionMs)
      .then((session) => {
        if (!cancelled) {
          sessionRef.current = session.sessionId;
          startedAtRef.current = Date.now();
        }
      })
      .catch(() => undefined);

    const timer = window.setInterval(() => {
      // Zamanlayıcı, öğe render edildikten sonra da çalışır; öğe ayrılmışsa
      // `element` null olur ve rapor atlanır.
      const video = videoRef.element;
      const sessionId = sessionRef.current;
      if (video === null || sessionId === null || video.paused || video.ended) return;

      const sourceMs = Math.round(video.currentTime * 1000);
      positionRef.current = sourceMs;

      /*
       * **404 alırsak oturumu yeniden kurarız.**
       *
       * Gerçek sunucuda 8 adet `progress` isteği 404 döndü ve konsol 8 hata
       * gösterdi. Sebep: izleme oturumları sunucuda **bellekte** tutuluyor
       * (`progress.ts`: `new Map()`), ben de bu oturumda sunucuyu defalarca
       * yeniden başlattım. Açık kalan sekme eski kimliği gönderiyor, sunucu
       * tanımıyor.
       *
       * Daha kötüsü: istemci 404'ü **yok sayıyordu**. Böylece yalnızca
       * gürültü değil, konum kaydı da sessizce kayboluyordu — kullanıcı filmi
       * izliyor, ilerleme kaydedilmiyordu.
       *
       * 404 "bu oturum artık yok" demektir; doğru yanıt yeni oturum açmaktır.
       */
      void api
        .reportProgress(sessionId, {
          sessionPositionMs: Date.now() - (startedAtRef.current ?? Date.now()),
          sourcePositionMs: sourceMs,
          state: 'playing',
        })
        .catch((error: unknown) => {
          if (!(error instanceof ApiError) || error.status !== 404) return;
          if (cancelled) return;
          sessionRef.current = null;
          void api
            .startSession(itemId, PROFILE_ID, sourceMs)
            .then((session) => {
              if (cancelled) return;
              sessionRef.current = session.sessionId;
              startedAtRef.current = Date.now();
            })
            .catch(() => undefined);
        });
    }, reportIntervalMs);

    return () => {
      cancelled = true;
      window.clearInterval(timer);

      const sessionId = sessionRef.current;
      if (sessionId !== null) {
        void api
          .reportProgress(sessionId, {
            sessionPositionMs: 0,
            sourcePositionMs: positionRef.current,
            state: 'stopped',
          })
          .then(() => {
            if (userId !== null) clearProgressDraft(userId, itemId);
          })
          .catch(() => undefined);
      }
      sessionRef.current = null;
      currentSourceUrlRef.current = null;
    };
  }, [
    videoRef.element,
    source,
    itemId,
    startPositionMs,
    reportIntervalMs,
    sessionRef,
    startedAtRef,
    positionRef,
    userId,
  ]);
}

/** Hazır olmadan önce gösterilen ultra modern durum ekranı. */
function Preparing({
  job,
  progressRatio,
  startPositionMs,
  isRemoteSource,
  onCancel,
}: {
  job: TranscodeJob | null;
  progressRatio: number | null;
  /** Akışın başlayacağı kaynak konumu (ms); 0 ise baştan. */
  startPositionMs: number;
  /** Kaynak soğuk katmanda (ev tüneli / rclone): ilk parçalar yavaş gelir. */
  isRemoteSource: boolean;
  onCancel: () => void;
}): ReactNode {
  const percent =
    progressRatio !== null && progressRatio > 0
      ? Math.min(100, Math.round(progressRatio * 100))
      : null;

  return (
    <div className="watch watch--preparing">
      <div className="watch__preparing-backdrop" aria-hidden="true" />

      <div className="watch__preparing-card">
        <div className="watch__preparing-spinner">
          <div className="watch__preparing-ring" />
          <div className="watch__preparing-ring watch__preparing-ring--pulse" />
          <span className="watch__preparing-icon">
            {job === null ? <SearchIcon size={34} /> : <BoltIcon size={34} />}
          </span>
        </div>

        <div className="watch__preparing-header">
          <h2 className="watch__preparing-title">
            {job === null ? 'Oynatma Kararı Hesaplanıyor' : 'Akış Hazırlanıyor…'}
          </h2>
          <p className="watch__preparing-desc">
            {job === null
              ? 'Sunucu donanımı ve cihaz profili uyumluluğu kontrol ediliyor.'
              : isRemoteSource
                ? 'Kaynak uzak diskten okunuyor; ilk parçalar için biraz süre gerekebilir.'
                : 'Yüksek kaliteli HLS akış parçaları sunucunuzda üretiliyor.'}
          </p>
          {startPositionMs > 0 && (
            <p className="watch__preparing-desc watch__preparing-resume">
              Kaldığınız yerden ({formatDuration(startPositionMs)}) devam edecek.
            </p>
          )}
        </div>

        <div className="watch__preparing-progress-wrap">
          <div className="watch__preparing-progress-header">
            <span className="watch__preparing-progress-label">
              {job === null ? 'Analiz ediliyor…' : 'Dönüştürme durumu'}
            </span>
            <span className="watch__preparing-progress-val">
              {percent !== null ? `%${percent}` : 'Hazırlanıyor…'}
            </span>
          </div>
          <div className="watch__preparing-bar-track">
            <div
              className={`watch__preparing-bar-fill ${percent === null ? 'watch__preparing-bar-fill--indeterminate' : ''}`}
              style={percent !== null ? { width: `${percent}%` } : undefined}
            />
          </div>
        </div>

        <div className="watch__preparing-badges">
          <span className="badge badge--accent">
            <BoltIcon size={12} />
            HLS Uyarlama
          </span>
          <span className="badge">
            <ClockIcon size={12} />
            4sn Segmentler
          </span>
          <span className="badge">
            <LockIcon size={12} />
            Yerel Sunucu
          </span>
        </div>

        <div className="watch__preparing-actions">
          <button
            type="button"
            className="button button--ghost watch__preparing-cancel"
            onClick={onCancel}
          >
            <CloseIcon size={14} />
            Oynatmayı İptal Et
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Oynatma hata ekranı.
 *
 * ## Neden ayrı bileşen?
 *
 * "Doğrudan oynatma" tercihi seçildiğinde ve dosya doğrudan oynatılamıyor,
 * burada **çıkış yolu** sunulur.
 *
 * Regresyon (kullanıcı bildirimi): tercih `localStorage`'da saklandığı için
 * "Geri dön" deyip tekrar denemek **aynı hata ekranını** getiriyordu —
 * kullanıcı seçimi değiştiremiyordu, çünkü seçim yalnız oynatma
 * ekranındaydı ve o ekrana hiç varılamıyordu.
 *
 * Çözüm: hata ekranı hem **sebebi** (gerekçe listesi) hem de tek tıkla
 * "Yine de oynat" eylemini gösterir; ayrıca tercih paneli burada da
 * erişilebilir.
 */
function WatchErrorScreen({
  message,
  decision,
  prefs,
  onQualityChange,
  onModeChange,
  onRetry,
  onBack,
}: {
  message: string;
  decision: PlaybackDecision | null;
  prefs: { quality: PlaybackQuality; mode: PlaybackMode };
  onQualityChange: (quality: PlaybackQuality) => void;
  onModeChange: (mode: PlaybackMode) => void;
  onRetry: () => void;
  onBack: () => void;
}): ReactNode {
  const isDirectBlocked = decision?.mode === 'unplayable';

  return (
    <div className="watch">
      <ErrorNotice message={message} />

      {isDirectBlocked && decision !== null && (
        <div className="watch__error-reasons">
          <ReasonList decision={decision} />
        </div>
      )}

      <div className="watch__actions">
        {!isDirectBlocked && (
          <button type="button" className="button button--primary" onClick={onRetry}>
            <RefreshIcon size={16} />
            Yeniden dene
          </button>
        )}
        {isDirectBlocked && (
          <button
            type="button"
            className="button button--primary"
            onClick={() => onModeChange('auto')}
          >
            <RefreshIcon size={16} />
            Yine de oynat
          </button>
        )}
        <button type="button" className="button" onClick={onBack}>
          Geri dön
        </button>
      </div>

      {isDirectBlocked && (
        <PlaybackPrefsPanel
          prefs={prefs}
          decision={decision}
          onQualityChange={onQualityChange}
          onModeChange={onModeChange}
        />
      )}
    </div>
  );
}

/**
 * Oynatma tercihleri paneli (ADR-011).
 *
 * ## İki ayrı seçim
 *
 *   - **Kalite**: `auto` · `original` · `1080p` · `720p` · `480p`
 *   - **Yöntem**: `auto` · `direct` (dönüştürme yok) · `transcode`
 *
 * ## CPU yükü ipucu
 *
 * Kullanıcının önceliği net: "sunucuyu yormamak". Seçimin sunucuyu ne kadar
 * yükleyeceği **açıkça yazılır**:
 *
 *   - doğrudan oynatma / kopya (remux) → **CPU yükü yok**
 *   - yeniden kodlama (x264, GPU yok) → **CPU yükü var**
 *
 * Böylece kullanıcı "720p" seçerken bunun bir encode demek olduğunu bilir.
 */
function PlaybackPrefsPanel({
  prefs,
  decision,
  audioTracks,
  selectedAudioIndex,
  onAudioChange,
  onQualityChange,
  onModeChange,
}: {
  prefs: { quality: PlaybackQuality; mode: PlaybackMode };
  decision: PlaybackDecision | null;
  audioTracks?: AudioTrack[] | undefined;
  selectedAudioIndex?: number | undefined;
  onAudioChange?: ((index: number) => void) | undefined;
  onQualityChange: (quality: PlaybackQuality) => void;
  onModeChange: (mode: PlaybackMode) => void;
}): ReactNode {
  const isDirect = decision?.mode === 'direct_play' || decision?.mode === 'direct_stream';
  const isCopy = decision?.targetProfile === 'remux';
  const serverLoad = isDirect || isCopy ? 'none' : 'high';

  return (
    <section className="watch__prefs" aria-label="Oynatma tercihleri">
      <div className="watch__prefs-row">
        <div className="watch__prefs-field">
          <label className="watch__prefs-label" htmlFor="watch-pref-quality">
            Kalite
          </label>
          <select
            id="watch-pref-quality"
            className="watch__prefs-select"
            value={prefs.quality}
            onChange={(event) => onQualityChange(event.target.value as PlaybackQuality)}
          >
            {(Object.keys(QUALITY_LABELS) as PlaybackQuality[]).map((quality) => (
              <option key={quality} value={quality}>
                {QUALITY_LABELS[quality]}
              </option>
            ))}
          </select>
        </div>

        <div className="watch__prefs-field">
          <label className="watch__prefs-label" htmlFor="watch-pref-mode">
            Yöntem
          </label>
          <select
            id="watch-pref-mode"
            className="watch__prefs-select"
            value={prefs.mode}
            onChange={(event) => onModeChange(event.target.value as PlaybackMode)}
          >
            {(Object.keys(MODE_LABELS) as PlaybackMode[]).map((mode) => (
              <option key={mode} value={mode}>
                {MODE_LABELS[mode]}
              </option>
            ))}
          </select>
        </div>

        {audioTracks !== undefined &&
          audioTracks.length > 1 &&
          selectedAudioIndex !== undefined &&
          onAudioChange !== undefined && (
            <div className="watch__prefs-field">
              <label className="watch__prefs-label" htmlFor="watch-pref-audio">
                Ses
              </label>
              <select
                id="watch-pref-audio"
                className="watch__prefs-select"
                value={selectedAudioIndex}
                onChange={(event) => onAudioChange(Number(event.target.value))}
              >
                {audioTracks.map((track) => (
                  <option key={track.index} value={track.index}>
                    {audioTrackLabel(track)}
                  </option>
                ))}
              </select>
            </div>
          )}
      </div>

      {/*
        Sunucu yükü göstergesi: kullanıcının "sunucuyu yormama" hedefini
        doğrudan ölçülebilir bir bilgiye çevirir.
      */}
      <p className={`watch__prefs-load watch__prefs-load--${serverLoad}`}>
        {serverLoad === 'none' ? (
          <>
            <BoltIcon size={14} />
            Sunucu CPU yüklenmiyor — dosya doğrudan aktarılıyor.
          </>
        ) : (
          <>
            <AlertIcon size={14} />
            Sunucu CPU yükü var: video yeniden kodlanıyor (VPS'te GPU yok).
          </>
        )}
      </p>
    </section>
  );
}

/** Karar gerekçeleri — "neden doğrudan oynatma değil?" sorusunun cevabı. */
function ReasonList({ decision }: { decision: PlaybackDecision }): ReactNode {
  return (
    <details className="watch__tech-details">
      <summary className="watch__tech-summary">
        <span className="watch__tech-icon">
          <SettingsIcon size={17} />
        </span>
        <span>Akış Tanılaması ({decision.mode === 'transcode' ? 'Dönüştürme' : 'Doğrudan'})</span>
      </summary>
      <div className="watch__tech-content">
        <p className="watch__tech-desc">
          Tarayıcı desteği ve sunucu donanım hızlandırma profiline göre en uygun akış yolu seçildi:
        </p>
        <div className="watch__reason-tags">
          {decision.reasons.map((reason) => (
            <div key={`${reason.code}-${reason.detail}`} className="watch__reason-tag">
              <code className="watch__reason-code">{reason.code}</code>
              <span className="watch__reason-text">{reason.detail}</span>
            </div>
          ))}
        </div>
      </div>
    </details>
  );
}

/**
 * Akış modunun insan diliyle adı (+ ikon).
 *
 * `ReactNode` döner, `string` değil: metinle birlikte ikon basmak için
 * gerekiyor. Dönüş tipini `string` bırakmak, ikonu ayrı bir koşulla ayrı
 * yazmaya zorlar ve iki yeri birbirinden kopuk tutmak hataya açık.
 */
function describeMode(decision: PlaybackDecision | null): ReactNode {
  if (decision === null) return 'Hazırlanıyor';
  if (decision.mode === 'unplayable') {
    return (
      <>
        <AlertIcon size={12} />
        Bu cihazda oynatılamıyor
      </>
    );
  }
  if (decision.mode === 'direct_play') {
    return (
      <>
        <BoltIcon size={12} />
        Orijinal kalite
      </>
    );
  }
  if (decision.mode === 'direct_stream') {
    return (
      <>
        <BoltIcon size={12} />
        Orijinal video
      </>
    );
  }
  if (decision.targetProfile === 'remux') {
    return (
      <>
        <BoltIcon size={12} />
        Orijinal video · kopyalanıyor
      </>
    );
  }
  const target = decision.targetProfile === null ? null : decision.targetProfile;
  return (
    <>
      <RefreshIcon size={12} />
      {target === null ? 'Dönüştürülüyor' : `${target} olarak dönüştürülüyor`}
    </>
  );
}

/**
 * İlk açılışta seçilecek altyazı: zorunlu olan, yoksa Türkçe, yoksa ilk yumuşak.
 *
 * **Yan dosya tercih edilir** (ADR-016): kullanıcı ayrı bir altyazı dosyası
 * koyduysa (çoğu kez Türkçe) o seçilir. Gömülü izler çoğu zaman
 * İngilizcedir ve kullanıcıyı rahatsız eder.
 */
function pickDefaultTrack(subtitles: Subtitle[]): string | null {
  const soft = subtitles.filter((track) => track.softPlayable);
  // `noUncheckedIndexedAccess` gereği ilk öğe opsiyoneldir.
  const first = soft[0];
  if (first === undefined) return null;

  const forced = soft.find((track) => track.forced);
  if (forced) return forced.key;
  const turkish = soft.find((track) => track.language === 'tur' || track.language === 'tr');
  if (turkish) return turkish.key;
  const external = soft.find((track) => track.external);
  return (external ?? first).key;
}

function isAudioMedia(item: Item, decision: PlaybackDecision | null): boolean {
  return (
    decision?.isAudio === true ||
    item.title.endsWith('.mp3') ||
    item.title.endsWith('.flac') ||
    item.title.endsWith('.wav') ||
    item.title.endsWith('.m4a') ||
    item.title.endsWith('.ogg')
  );
}

function AudioVisualizer({ item }: { item: Item }): ReactNode {
  return (
    <div className="watch__audio-visual" aria-hidden="true">
      <div className="watch__audio-disc">
        <span className="watch__audio-icon">
          <MusicIcon size={22} />
        </span>
      </div>
      <div className="watch__audio-info">
        <span className="watch__audio-badge">Müzik Çalıyor</span>
        <h3 className="watch__audio-title">{displayTitle(item)}</h3>
      </div>
    </div>
  );
}

function WatchBadges({
  item,
  decision,
  position,
  forceCompatibility,
  onToggleCompatibility,
}: {
  item: Item;
  decision: PlaybackDecision | null;
  position: number;
  forceCompatibility: boolean;
  onToggleCompatibility: () => void;
}): ReactNode {
  return (
    <div className="watch__badges">
      <span className="badge badge--accent">{describeMode(decision)}</span>
      <button
        type="button"
        className={`badge ${forceCompatibility ? 'badge--warn' : 'badge--success'}`}
        style={{ cursor: 'pointer', border: 'none' }}
        onClick={onToggleCompatibility}
        title={
          forceCompatibility
            ? 'Uyumluluk modu açık (SDR transcode). Kapatmak için tıklayın.'
            : 'Uyumluluk modu kapalı; sunucu kararına göre doğrudan, remux veya transcode uygulanır. Eski cihazlarda takılma olursa açın.'
        }
      >
        {forceCompatibility ? (
          <>
            <RefreshIcon size={12} />
            Uyumluluk: Açık
          </>
        ) : (
          <>
            <BoltIcon size={12} />
            Uyumluluk: Kapalı
          </>
        )}
      </button>
      {decision?.layer === 'cold' && (
        <span
          className="badge badge--tunnel"
          title="Ev sunucusundan şifreli WireGuard tüneli üzerinden aktarılıyor"
        >
          <LockIcon size={12} />
          Ev Tüneli
        </span>
      )}
      {item.year !== null && <span className="badge">{item.year}</span>}
      {item.durationMs !== null && <span className="badge">{formatDuration(item.durationMs)}</span>}
      {position > 0 && item.durationMs && (
        <span className="badge badge--muted">
          %{Math.round((position / item.durationMs) * 100)} izlendi ({formatDuration(position)})
        </span>
      )}
    </div>
  );
}
